import { useState } from "react";

import {
  TextField,
  Select,
  Button,
  Checkbox,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";

import { OIB_DIGITS_LIMIT, OIB_REGEX } from "../../constants";
import type { IOibIdentitySectionProps } from "./types";

const EMPTY_SELECT_DATA = codeBookToSelectData([]);

export const OibIdentitySection = ({
  isOibConnected,
  oibValue,
  onOibChange,
  oibErrorText,
  isOibReadonly,
  showConnectButton,
  onConnectClick,
  isConnected,
  oibSecondaryAction,
  personData,
  alternativeCodeErrorText,
  alternativeTypeErrorText,
  alternativeReasonErrorText,
  lastNameErrorText,
  isLastNameReadonly,
  lastNameHelper,
  firstNameErrorText,
  isFirstNameReadonly,
  firstNameHelper,
  defaultIsNoOibChecked = false,
  onNoOibChange,
  showNoOibCheckbox = true,
  noOibCheckboxLabel,
  oibLabel,
  lastNameLabel,
  firstNameLabel,
  disabled,
}: IOibIdentitySectionProps) => {
  const {
    alternativeCodeValue,
    onAlternativeCodeChange,
    alternativeTypeId,
    alternativeTypeOptions,
    onAlternativeTypeChange,
    alternativeReasonId,
    alternativeReasonOptions,
    onAlternativeReasonChange,
    lastNameValue,
    onLastNameChange,
    firstNameValue,
    onFirstNameChange,
  } = personData;

  const { t } = useTranslation();

  const [isNoOibChecked, setIsNoOibChecked] = useState<boolean>(
    defaultIsNoOibChecked
  );

  const handleToggleNoOib = () => {
    const nextValue = !isNoOibChecked;

    setIsNoOibChecked(nextValue);

    if (onNoOibChange) {
      onNoOibChange(nextValue);
    }
  };

  const shouldShowOib = showNoOibCheckbox ? !isNoOibChecked : true;
  const shouldShowAlternativeIdentity = showNoOibCheckbox && isNoOibChecked;

  const isOibDataValid =
    OIB_REGEX.test(oibValue ?? "") && !!lastNameValue && !!firstNameValue;

  const isConnectDisabled = !(oibValue !== undefined && isOibDataValid);

  const handleOibChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value
      .replace(/\D/g, "")
      .slice(0, OIB_DIGITS_LIMIT);
    onOibChange(digitsOnly);
  };

  return (
    <>
      <Row style={{ alignItems: "end" }}>
        <Col md={6}>
          {showNoOibCheckbox && !isOibConnected && (
            <Checkbox
              label={noOibCheckboxLabel ?? t("person.noOib")}
              checked={isNoOibChecked}
              onChange={handleToggleNoOib}
              disabled={disabled}
            />
          )}
        {shouldShowOib && showConnectButton && isConnectDisabled && (
            <Typography variant="base" color="primary">
              {t("person.oibConnectHint")}
            </Typography>
          )}
          {shouldShowOib && (
            <TextField
              label={oibLabel ?? t("person.oib")}
              value={oibValue}
              readonly={isOibReadonly}
              onChange={handleOibChange}
              plaintext={isOibConnected}
              errorText={oibErrorText}
              required={shouldShowOib}
              maxLength={OIB_DIGITS_LIMIT}
            />
          )}
        </Col>
        <Col
          md={4}
          className="d-flex flex-wrap align-items-end"
          style={{ gap: "10px" }}
        >
          {shouldShowOib && showConnectButton && (
            <Button
              onClick={onConnectClick}
              disabled={isConnectDisabled || disabled}
              size="sm"
              className="text-nowrap mb-3"
            >
              {isConnected
                ? t("common.schoolCandidateModel.disconnectOibSystem")
                : t("common.schoolCandidateModel.connectOibSystem")}
            </Button>
          )}

          {shouldShowOib && oibSecondaryAction}

          
        </Col>
        
      </Row>

      {shouldShowAlternativeIdentity && (
        <Row>
          <Col md={4}>
            <TextField
              label={t("person.alternativeDocumentNumber")}
              value={alternativeCodeValue ?? ""}
              readonly={isOibConnected}
              onChange={(event) =>
                onAlternativeCodeChange?.(event.target.value)
              }
              plaintext={isOibConnected}
              errorText={alternativeCodeErrorText}
              disabled={disabled}
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("person.alternativeDocumentType")}
              name="alternativeType"
              value={alternativeTypeId ?? undefined}
              onChange={(event) =>
                onAlternativeTypeChange?.(event.target.value)
              }
              selectData={alternativeTypeOptions ?? EMPTY_SELECT_DATA}
              readonly={isOibConnected}
              emptyOption={{ value: undefined, text: undefined }}
              borderless={isOibConnected}
              errorText={alternativeTypeErrorText}
              disabled={disabled}
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("person.alternativeDocumentReason")}
              name="alternativeReason"
              value={alternativeReasonId ?? undefined}
              onChange={(event) =>
                onAlternativeReasonChange?.(event.target.value)
              }
              selectData={alternativeReasonOptions ?? EMPTY_SELECT_DATA}
              readonly={isOibConnected}
              emptyOption={{ value: undefined, text: undefined }}
              borderless={isOibConnected}
              errorText={alternativeReasonErrorText}
              disabled={disabled}
            />
          </Col>
        </Row>
      )}

      <Row>
        <Col md={4}>
          <TextField
            label={lastNameLabel ?? t("person.lastName")}
            value={lastNameValue}
            readonly={isLastNameReadonly}
            onChange={(event) => onLastNameChange(event.target.value)}
            plaintext={isOibConnected}
            errorText={lastNameErrorText}
            required={shouldShowOib}
            disabled={disabled}
          />
          {lastNameHelper}
        </Col>
        <Col md={4}>
          <TextField
            label={firstNameLabel ?? t("person.firstName")}
            value={firstNameValue}
            readonly={isFirstNameReadonly}
            onChange={(event) => onFirstNameChange(event.target.value)}
            plaintext={isOibConnected}
            errorText={firstNameErrorText}
            required={shouldShowOib}
            disabled={disabled}
          />
          {firstNameHelper}
        </Col>
      </Row>
    </>
  );
};
